<template>
  <div :class="['paginated-list flex flex-col gap-4 h-full overflow-hidden', className]">
    <div class="w-full h-full overflow-auto scroll-bar pr-1">
      <div class="w-full">
        <ListView
          :listItems="paginatedItems"
          :showTitle="false"
          :showSearch="true"
          :bordered="bordered"
          class="h-full overflow-y-auto"
          :itemClass="itemClass"
          :listeners="$listeners"
          :empty-text="$t('pages.localization.no_translations_found')"
          @handleFilterResult="handleFilterResult"
        >
          <template #listheader>
            <slot name="listheader"></slot>  
          </template>
          <template #item="{ item }">
            <slot name="item" :item="item">{{ item?.label || '---' }}</slot>
          </template>
        </ListView>
      </div>
    </div>

    <div class="paginated-list--footer w-full gap-2 items-center flex justify-between px-2 pr-4">
      <div class="flex flex-3 w-full">
        <slot name="footer-start">
          <PerPage
            v-model="perPageModel"
          />
        </slot>
      </div>
        <div
          class="flex flex-2 w-full"
          :class="{
            'justify-center': $slots['footer-right'],
            'justify-end': !$slots['footer-right'],
          }"
        >
          <slot name="footer-middle">
            <Pagination
              v-if="pagesCount > 1"
              v-model="pageModel"
              :totalItemsCount="filteredItems.length"
              :page-limit="pagination.perPage"
              @page-changed="changePage" 
            />
          </slot>
        </div>
        <div v-if="$slots['footer-right']" class="flex flex-2 w-full justify-end">
          <slot name="footer-right"/>
        </div>
    </div>
  </div>
</template>

<script>
import ListView from "@shared/components/lists";
import Pagination from './pagination.vue';
import PerPage from './per-page.vue';

export default {
  name: 'PaginatedList',

  components: {
    ListView,
    Pagination,
    PerPage,
  },

  props: {
    items: {
      type: Array,
      default: () => [],
    },
    filterItemsFn: Function,
    bordered: {
      type: Boolean,
      default: false,
    },
    className: {
      type: String, 
      default: ''
    },
    itemClass: {
      type: String, 
      default: ''
    },
    page: {
      type: Number,
      default: null,
    },
    perPage: {
      type: Number,
      default: null,
    },
  },

  data() {
    return {
      searchValue: '',
      pagination: {
        page: 1,
        perPage: 10,
      }
    };
  },

  watch: {
    paginatedItems: {
      handler() {
        this.sharePagination();
      },
      deep: true,
    },
  },

  computed: {
    perPageModel: {
      get() {
        return this.perPage ?? this.pagination.perPage;
      },
      set(value) {
        this.pagination.perPage = value;
        this.pageModel = 1;
        this.$emit('update:perPage', value);
      }
    },
    pageModel: {
      get() {
        return this.page ?? this.pagination.page;
      },
      set(value) {
        this.pagination.page = value;
        this.$emit('update:page', this.pagination.page);
      }
    },
    filteredItems() {
      if(this.searchValue && this.searchValue.length && typeof this.filterItemsFn == 'function') {
        return this.filterItemsFn({ array: this.items, filter: this.searchValue });
      }
      return this.items;
    },
    offset() {
      return this.perPageModel * (this.pageModel - 1);
    },
    pagesCount() {
      return Math.ceil(this.filteredItems.length / this.perPageModel);
    },
    paginatedItems() {
      const startIndex = this.offset;
      const endIndex = startIndex + this.perPageModel;
      return this.filteredItems.slice(startIndex, endIndex);
    },
  },

  mounted() {
    this.changePage(this.pagination);
    this.sharePagination();
  },

  methods: {
    handleFilterResult(value) {
      this.searchValue = value;
      this.pagination.page = 1;
      this.pageModel = 1;
    },

    changePage(pagination) {
      this.pagination = pagination;
    },

    sharePagination() {
      this.$emit('update:pagination', {
        ...this.pagination,
        pagesCount: this.pagesCount,
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.paginated-list--footer {
  display: flex;
  width: 100%;
}
</style>
